import { Component, Input } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { PonderDataClient } from "@apiserver-rest/ponder-data-client";
import { DataResponse, Deck, GetDecksResponse, GetUserResourceVisibilityResponse, UserProtectedResource } from "@apiserver-rest/ponder-data-client-base";
import { AuthService } from "@ponder-core/auth/auth.service";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { addSegmentToList, initializeUserSegmentArray } from "@ponder-features/header/navbar/navbar.component";
import { LoadingComponent } from "@ponder-shared/loading/loading.component";
import { Subscription, take } from "rxjs";
import { DeckPreviewGroupComponent } from "./deck-preview/deck-preview-group.component";
import { SvgIcon, SvgIconMap } from "@ponder-shared/utils/svgIcons";
import { DeckCreateModalComponent } from "./deck/tools/deck-create-modal.component";
import { DeckChangeGroupEvent } from "@ponder-core/events/deck.events";
import { DataBroker } from "@ponder-core/data/dataBroker";
import { DeckGroupDataRequest } from "@ponder-core/data/deck.data";
import { DeckImportModalComponent } from "./deck/tools/deck-import-modal.component";
import { AuthSession } from "@ponder-core/auth/auth-session";
import { UnauthorizedComponent } from "@ponder-shared/components/unauthorized.component";
import { RoleAndPermissions } from "@ponder-core/auth/role-permissions";
import { NgClass } from "@angular/common";

/**
 * Displays all of the decks owned by a specific user.
 * Also exposes tools to add, delete, and edit decks.
 */
@Component({
    imports: [LoadingComponent, DeckPreviewGroupComponent, DeckCreateModalComponent, DeckImportModalComponent, UnauthorizedComponent, NgClass],
    templateUrl: "./user-decks.component.html"
})
export class UserDecksComponent {
    @Input() public username!: string;

    public isLoading: boolean = true;
    public loadingSuccess: boolean = false;
    public isOwnDecks: boolean = false;
    public isVisible?: boolean = undefined;
    public authSession?: AuthSession;

    public readonly addSvgIcon: string = SvgIconMap.get(SvgIcon.New)!;
    public readonly importSvgIcon: string = SvgIconMap.get(SvgIcon.Import)!;

    public deckGroups: DeckGroupRef[] = []
    public totalDecks: number = 0;
    public canAddDecks: boolean = false;

    public userDeckLimit: number = 0;

    private deckChangeGroupSub!: Subscription;
    private requestDeckGroupsSub!: Subscription;
    private deckDeletedSub!: Subscription;

    public constructor(
        private ponderDataClient: PonderDataClient, 
        private pageTitle: Title,
        private events: EventBroker,
        private data: DataBroker,
        private auth: AuthService) { 
            this.deckChangeGroupSub = this.events.deck.deckChangeGroupEvent.subscribe({
                next: (event: DeckChangeGroupEvent) => {
                    if (event.fromGroup != event.toGroup) {
                        this.removeDeckFromGroup(event.deck, event.fromGroup, false);
                        this.addDeckToGroup(event.deck, event.toGroup, true);
                    }
                }
            });

            this.requestDeckGroupsSub = this.data.deck.requestDeckGroups.subscribe({
                next: (request: DeckGroupDataRequest) => {
                    // When a request comes in for the list of groups, send it back.
                    request(this.deckGroups.map(g => g.group));
                }
            });

            this.deckDeletedSub = this.events.deck.deckDeletedEvent.subscribe({
                next: () => {
                    this.totalDecks = Math.max(0, this.totalDecks - 1);
                    this.canAddDecks = this.totalDecks < this.userDeckLimit;
                }
            });
            
            this.events.ui.toggleSortFilterBarEvent.emit(false);
        }

    public ngOnDestroy() {
        this.deckChangeGroupSub.unsubscribe();
        this.requestDeckGroupsSub.unsubscribe();
        this.deckDeletedSub.unsubscribe();
    }

    // -------------------------------------------------------------------------------------
    // Life Cycle
    // -------------------------------------------------------------------------------------

    public ngOnInit(): void {
        this.auth.getSessionAsync().then((authSession?: AuthSession) => {
            this.authSession = authSession;

            if (authSession?.username && (authSession.username == this.username)) {
                this.isOwnDecks = true;
                this.isVisible = true;
            }

            if (this.isOwnDecks) {
                this.auth.getRoleAndPermissions().then((roleAndPermissions?: RoleAndPermissions) => {
                    if (roleAndPermissions) {
                        this.userDeckLimit = roleAndPermissions.limits?.deckCount ?? 0;
                    }
                })
                .finally(() => {
                    this.fetchUserDeckVisbility();
                });
            }
            else {
                this.fetchUserDeckVisbility();
            }

        });

        this.pageTitle.setTitle(`Ponder > ${this.username} > Decks`);
        this.sendNavbarUpdate();
    }

    private sendNavbarUpdate(): void {
        var segments = initializeUserSegmentArray(this.username);
        addSegmentToList(segments, "Decks", "Decks");
        
        this.events.ui.navbarUpdateEvent.emit(segments);
    }

    private fetchUserDeckVisbility(): void {
        this.ponderDataClient
            .userResourceVisibility(this.username, UserProtectedResource.Decks)
            .pipe(take(1))
            .subscribe({
                next: (response: GetUserResourceVisibilityResponse) => {
                    if (response.success && response.isVisible) {
                        this.isVisible = true;
                        this.fetchUserDecks();
                    }
                }
            })
            .add(() => {
                if (!this.isVisible) {
                    this.isVisible = false;
                }
            });
    }

    private fetchUserDecks(): void {
        this.ponderDataClient
            .decks(this.username)
            .pipe(take(1))
            .subscribe({
                next: (response: GetDecksResponse) => {
                    if (response.success) {
                        this.loadingSuccess = true;
                        this.totalDecks = response.decks?.length ?? 0;
                        this.canAddDecks = this.totalDecks < this.userDeckLimit;
                        
                        response.decks?.forEach(d => {
                            this.addDeckToGroup(d, d.group!, false);
                        });

                        this.sortGroups();
                    }
                }
            })
            .add(() => { this.isLoading = false; });
    }

    // -------------------------------------------------------------------------------------
    // Group Actions
    // -------------------------------------------------------------------------------------

    public trackGroupComponent(ref: DeckPreviewGroupComponent): void {
        var group = this.getGroup(ref.name);
        group.component = ref;
    }

    private addDeckToGroup(deck: Deck, groupName: string, sort: boolean): void {
        var group = this.getGroup(groupName);

        group.decks.push(deck);

        if (sort) {
            this.sortGroups();
            group.component?.sortDecks();
        }
    }

    private removeDeckFromGroup(deck: Deck, groupName: string, sort: boolean): void {
        var group = this.getGroup(groupName);

        if (group) {
            var index = group.decks.findIndex(d => d.id == deck.id);

            if (index >= 0) {
                group.decks.splice(index, 1);
            }

            if (sort) {
                this.sortGroups();
                group.component?.sortDecks();
            }
        }
    }

    private getGroup(name: string): DeckGroupRef {
        for (let i = 0; i < this.deckGroups.length; ++i) {
            if (this.deckGroups[i].group == name) {
                return this.deckGroups[i];
            }
        }

        var newGroup: DeckGroupRef = {
            group: name,
            decks: []
        };

        this.deckGroups.push(newGroup);

        return newGroup;
    }

    private sortGroups(): void {
        this.deckGroups.sort((a, b) => a.group.localeCompare(b.group));
    }

    public changeGroupName(from: string, to: string): void {
        if (from == to) {
            return;
        }

        var group = this.getGroup(from);

        if (group) {
            this.changeGroupNameInternal(group, to);

            this.ponderDataClient
                .deckGroupRename(from, to)
                .pipe(take(1))
                .subscribe({
                    next: (event: DataResponse) => {
                        if (!event.success) {
                            this.changeGroupNameInternal(group, from);
                        }
                    },
                    error: () => {
                        this.changeGroupNameInternal(group, from);
                    }
                });
        }
    }

    private changeGroupNameInternal(group: DeckGroupRef, to: string) {
        group.group = to;
        group.component!.name = to;
        
        group.decks.forEach(d => {
            d.group = to;
        });

        this.sortGroups();
    }
    
    // -------------------------------------------------------------------------------------
    // Input Actions
    // -------------------------------------------------------------------------------------

    public onCreateDeckClicked(): void {
        this.events.deck.showDeckCreateModalEvent.emit();
    }

    public onImportDeckClicked(): void {
        this.events.deck.showDeckImportModalEvent.emit();
    }

    // -------------------------------------------------------------------------------------
    // Filtering
    // -------------------------------------------------------------------------------------

    private currentGameSelection = "All";
    private currentDeckTypeSelection = "All";
    private currentVisibilitySelection = "All";

    public getGameOptions(): string[] {
        var uniqueOptions = new Set<string>;

        this.deckGroups?.forEach(g => {
            g.decks?.forEach(d => {
                uniqueOptions.add(d.game);
            });
        });

        var sortedOptions = [...uniqueOptions].sort();
        sortedOptions.unshift("All Games");

        return sortedOptions;
    }

    public getDeckTypeOptions(): string[] {
        var uniqueOptions = new Set<string>;

        this.deckGroups?.forEach(g => {
            g.decks?.forEach(d => {
                uniqueOptions.add(d.type!);
            });
        });

        var sortedOptions = [...uniqueOptions].sort();
        sortedOptions.unshift("All Types");

        return sortedOptions;
    }

    public onGameTypeOptionSelected(value: string): void {
        if (value === this.currentGameSelection) {
            return;
        }

        this.currentGameSelection = value;
        this.updateDecksVisibility();
    }

    private updateDecksVisibility(): void {
        this.deckGroups?.forEach(g => g.component?.updateDeckVisibility(this));
    }

    public onDeckTypeOptionSelected(value: string): void {
        if (value === this.currentDeckTypeSelection) {
            return;
        }

        this.currentDeckTypeSelection = value;
        this.updateDecksVisibility();
    }

    public onVisibilityOptionSelected(value: string): void {
        if (value === this.currentVisibilitySelection) {
            return;
        }

        this.currentVisibilitySelection = value;
        this.updateDecksVisibility();
    }

    public isDeckVisible(deck: Deck): boolean {
        if (this.currentGameSelection !== "All") {
            if (deck.game !== this.currentGameSelection) {
                return false;
            }
        }

        if (this.currentDeckTypeSelection !== "All") {
            if (deck.type !== this.currentDeckTypeSelection) {
                return false;
            }
        }

        if (this.currentVisibilitySelection !== "All") {
            if (deck.visibility !== this.currentVisibilitySelection) {
                return false;
            }
        }

        return true;
    }
}

type DeckGroupRef = {
    group: string;
    component?: DeckPreviewGroupComponent;
    decks: Deck[]
}